import { WEEKLY_PLAN_DAYS, type WeeklyPlanDay, type WeeklyPlanRow } from '@gb-ticket/shared-types';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export const DAY_LABEL: Record<WeeklyPlanDay, string> = {
  monday: 'Montag',
  tuesday: 'Dienstag',
  wednesday: 'Mittwoch',
  thursday: 'Donnerstag',
  friday: 'Freitag',
  saturday: 'Samstag',
};

export const DAY_SHORT: Record<WeeklyPlanDay, string> = {
  monday: 'Mo',
  tuesday: 'Di',
  wednesday: 'Mi',
  thursday: 'Do',
  friday: 'Fr',
  saturday: 'Sa',
};

export type DraftRow = {
  key: string;
  id?: string;
  siteId: string;
  groupId: string | null;
  userId: string | null;
  freeName: string | null;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  assignment: string | null;
  cells: Partial<Record<WeeklyPlanDay, string>>;
};

export type DraftGroup = {
  key: string;
  id: string;
  siteId: string;
  name: string;
  sortOrder: number;
};

export function dayHeaderClass(today: boolean): string {
  return today ? 'bg-status-info text-status-info-foreground' : '';
}

export function splitDisplayName(displayName: string): {
  firstName: string | null;
  lastName: string | null;
} {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { firstName: null, lastName: null };
  }
  if (parts.length === 1) {
    return { firstName: null, lastName: parts[0] ?? null };
  }
  return { firstName: parts[0] ?? null, lastName: parts.slice(1).join(' ') };
}

export function toDraft(rows: WeeklyPlanRow[]): DraftRow[] {
  return rows.map((row) => {
    const fallback = splitDisplayName(row.displayName);
    const free = row.freeName !== null;
    return {
      key: row.id,
      id: row.id,
      siteId: row.siteId,
      groupId: row.groupId ?? null,
      userId: row.userId,
      freeName: row.freeName,
      displayName: row.displayName,
      firstName: free ? null : (row.firstName ?? fallback.firstName),
      lastName: free ? (row.freeName ?? row.lastName) : (row.lastName ?? fallback.lastName),
      assignment: row.assignment ?? null,
      cells: Object.fromEntries(row.cells.map((cell) => [cell.day, cell.text])),
    };
  });
}

export function toGroups(
  groups: { id: string; siteId: string; name: string; sortOrder: number }[],
): DraftGroup[] {
  return groups.map((group) => ({
    key: group.id,
    id: group.id,
    siteId: group.siteId,
    name: group.name,
    sortOrder: group.sortOrder,
  }));
}

export function boardSnapshot(rows: DraftRow[], groups: DraftGroup[]): string {
  return JSON.stringify({
    groups: groups.map((group) => ({
      siteId: group.siteId,
      id: group.id,
      name: group.name.trim(),
      sortOrder: group.sortOrder,
    })),
    rows: rows.map((row) => ({
      siteId: row.siteId,
      groupId: row.groupId,
      userId: row.userId,
      freeName: row.freeName?.trim() ?? '',
      assignment: row.assignment?.trim() ?? '',
      cells: WEEKLY_PLAN_DAYS.flatMap((day) => {
        const text = row.cells[day]?.trim() ?? '';
        return text ? [[day, text] as const] : [];
      }),
    })),
  });
}

export function rowTitle(row: DraftRow): string {
  return row.freeName ? `${row.displayName} ohne Konto` : row.displayName;
}

export function lastNameOf(row: DraftRow): string {
  return row.lastName ?? row.displayName;
}

export type PersonChrome = 'own' | 'selected' | 'plain';

export function personChrome(
  row: DraftRow,
  meId: string | undefined,
  selectedIds: readonly string[],
): PersonChrome {
  if (row.userId !== null && row.userId === meId) {
    return 'own';
  }
  if (row.userId !== null && row.freeName === null && selectedIds.includes(row.userId)) {
    return 'selected';
  }
  return 'plain';
}

export const CARD_CLASS: Record<PersonChrome, string> = {
  own: 'rounded-md border border-l-4 border-l-primary p-3',
  selected: 'rounded-md border border-l-2 border-l-muted-foreground/30 bg-muted/20 p-3',
  plain: 'rounded-md border p-3',
};

export const NAME_CELL_CLASS: Record<PersonChrome, string> = {
  own: 'sticky left-0 z-10 bg-primary/5 border-l-4 border-l-primary text-primary',
  selected: 'sticky left-0 z-10 bg-muted/40 border-l-2 border-l-muted-foreground/30',
  plain: 'sticky left-0 z-10 bg-background',
};

export const TIME_ROW_CLASS: Record<PersonChrome, string> = {
  own: 'border-l-4 border-l-primary bg-primary/5 text-primary',
  selected: 'border-l-2 border-l-muted-foreground/30 bg-muted/40',
  plain: '',
};

export const LIST_ROW_CLASS: Record<PersonChrome, string> = {
  own: 'border-l-2 border-l-primary',
  selected: 'bg-muted',
  plain: '',
};

export const LIST_CELL_CLASS: Record<PersonChrome, string> = {
  own: 'border-l-2 border-l-primary bg-background',
  selected: 'bg-muted',
  plain: 'bg-background',
};

export function portalPeople(rows: DraftRow[]): (DraftRow & { userId: string })[] {
  const seen = new Set<string>();
  const people: (DraftRow & { userId: string })[] = [];
  for (const row of rows) {
    const userId = row.userId;
    if (userId === null || row.freeName !== null || seen.has(userId)) {
      continue;
    }
    seen.add(userId);
    people.push({ ...row, userId });
  }
  return people;
}

export function isRecentCell(rows: WeeklyPlanRow[], row: DraftRow, day: WeeklyPlanDay): boolean {
  return (
    rows
      .find((entry) => entry.id === row.id)
      ?.cells.some((cell) => cell.day === day && cell.recent) ?? false
  );
}

function cellTone(recent: boolean): string | undefined {
  if (recent) {
    return 'border-status-warning-border bg-status-warning text-status-warning-foreground dark:border-status-warning-border dark:bg-status-warning dark:text-status-warning-foreground';
  }
  return undefined;
}

function cellAriaLabel(displayName: string, day: WeeklyPlanDay, recent: boolean): string {
  const base = `${displayName} ${DAY_LABEL[day]}`;
  return recent ? `${base} geändert` : base;
}

export function assignmentField(
  row: DraftRow,
  writable: boolean,
  patchRow: (key: string, recipe: (row: DraftRow) => DraftRow) => void,
) {
  if (!writable) {
    return <span>{row.assignment ?? ''}</span>;
  }
  return (
    <Input
      aria-label={`${row.displayName} Einsatz als`}
      value={row.assignment ?? ''}
      placeholder="z. B. Monteur"
      onChange={(event) =>
        patchRow(row.key, (current) => ({
          ...current,
          assignment: event.target.value,
        }))
      }
    />
  );
}

export function cellField(
  row: DraftRow,
  day: WeeklyPlanDay,
  writable: boolean,
  recent: boolean,
  patchRow: (key: string, recipe: (row: DraftRow) => DraftRow) => void,
  presentation: 'plain' | 'clamp' = 'plain',
) {
  const stored = row.cells[day] ?? '';
  const label = cellAriaLabel(row.displayName, day, recent);
  const tone = cellTone(recent);
  if (!writable) {
    if (presentation === 'clamp') {
      return (
        <span
          aria-label={label}
          title={stored || undefined}
          className={cn('line-clamp-2', !stored && 'text-muted-foreground', tone)}
        >
          {stored || '—'}
        </span>
      );
    }
    return (
      <span aria-label={label} className={tone}>
        {stored}
      </span>
    );
  }
  return (
    <Input
      aria-label={label}
      title={presentation === 'clamp' ? stored : undefined}
      className={cn(tone, presentation === 'clamp' && 'line-clamp-2')}
      value={stored}
      onChange={(event) =>
        patchRow(row.key, (current) => ({
          ...current,
          cells: { ...current.cells, [day]: event.target.value },
        }))
      }
    />
  );
}
